/**
 * Cloudinary URL transformation utilities.
 * Inserts transformation parameters into Cloudinary URLs dynamically,
 * avoiding the need to store multiple versions in the database.
 */

const CLOUDINARY_HOST = 'res.cloudinary.com';
const UPLOAD_SEGMENT = '/image/upload/';

/**
 * Returns true if the URL is a Cloudinary image URL without existing transformations.
 */
function isTransformableCloudinaryUrl(url) {
  if (!url || typeof url !== 'string') return false;
  if (!url.includes(CLOUDINARY_HOST)) return false;
  if (!url.includes(UPLOAD_SEGMENT)) return false;
  // If it already has transformations (e.g. /upload/w_400,.../) skip
  const afterUpload = url.split(UPLOAD_SEGMENT)[1] || '';
  const firstSegment = afterUpload.split('/')[0];
  // Transformation params contain '=' or '_', version starts with 'v' followed by digits
  const hasTransformation = firstSegment.includes('_') && !firstSegment.startsWith('v');
  return !hasTransformation;
}

/**
 * Inserts Cloudinary transformation params into a URL.
 * Input:  .../image/upload/v123/folder/img.jpg
 * Output: .../image/upload/{params}/v123/folder/img.jpg
 */
function applyTransformation(url, params) {
  if (!isTransformableCloudinaryUrl(url)) return url;
  return url.replace(UPLOAD_SEGMENT, `${UPLOAD_SEGMENT}${params}/`);
}

/**
 * Returns a thumbnail URL optimized for product grids and catalog cards.
 * ~400x500px, fill crop, quality 65, auto format.
 */
export function getCloudinaryThumbnail(url) {
  return applyTransformation(url, 'w_400,h_500,c_fill,q_65,f_auto');
}

/**
 * Returns a medium URL optimized for product detail pages.
 * Max 900px wide, limit crop (no enlargement), quality 80, auto format.
 */
export function getCloudinaryMedium(url) {
  return applyTransformation(url, 'w_900,c_limit,q_80,f_auto');
}
